import { useEffect, useState } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import reportBook from "../../../assets/images/life-journey/report-book.jpg";
import { useEvergreenCountdown } from "../../..//hooks/useEvergreenCountdown";
import {
  CheckCircle,
  FileText,
  TrendingUp,
  Clock
} from "lucide-react";

export const FinalCTASection = () => {
  const handleScrollToPricing = () => {
    const element = document.getElementById("pricing-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const [hideFooter, setHideFooter] = useState(false);

  useEffect(() => {
    const section = document.getElementById("pricing-section");
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setHideFooter(entry.isIntersecting); // true => hide, false => show
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const { display } = useEvergreenCountdown("lifeReportOfferEnd", 4 * 60 * 60 * 1000);

  const benefits = [
    {
      icon: TrendingUp,
      title: "सही Career Direction & Life Purpose",
      description: ""
    },
    {
      icon: CheckCircle,
      title: "Financial Stability & Growth solutions",
      description: ""
    },
    {
      icon: FileText,
      title: "Peace of Mind & Confidence",
      description: ""
    }
  ];

  const reportFeatures = [
    "Based on Ancient Vedic Astrology Principles",
    "Includes Precise Planetary Calculations",
    "Provides Personalised Remedies",
    "Trusted by Thousands across India & abroad"
  ];

  return (
    <section className="bg-gradient-to-b from-cosmic-blue/10 to-background-blue">
      <div className="container mx-auto px-6 pt-8">
        {/* Benefits Section */}
        <div className="mb-20">
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground text-center mb-16">
            This Report Will Help You Achieve
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="p-6 border-cosmic-gold/20 bg-card-blue/50 backdrop-blur-sm hover:shadow-[0_0_20px_hsl(var(--mystic-gold)/0.2)] transition-all duration-300">
                <benefit.icon className="w-12 h-12 text-cosmic-gold mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {benefit.description}
                </p>
              </Card>
            ))}
          </div>
        </div>

        {/* Report Special Features */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left Column - Report Image */}
          <div className="relative">
            <img loading="lazy"
              src={reportBook}
              alt="Premium Life Path Report"
              className="w-full max-w-md mx-auto rounded-2xl shadow-[0_0_40px_hsl(var(--mystic-gold)/0.3)]"
            />
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-cosmic-gold/30 to-cosmic-glow/30 rounded-full blur-xl"></div>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-sm uppercase tracking-wider text-cosmic-gold mb-4">This Report Can Be Life-Journey For You,</h2>
              <h3 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Here is Why it's So Special
              </h3>
            </div>

            {/* Features List */}
            <div className="space-y-4">
              {reportFeatures.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-cosmic-gold flex-shrink-0" />
                  <span className="text-lg font-semibold text-foreground">{feature}</span>
                </div>
              ))}
            </div>

            <p className="text-muted-foreground text-lg">
              Think of this as more than just a purchase. It's an investment in your personal development.
              For a small investment, you'll gain insights that can transform your life, helping you make
              better decisions, achieve your goals, and find lasting happiness.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <Card className="p-8 border-cosmic-gold/30 bg-card-blue/50 backdrop-blur-sm mb-12">
          <h2 className="text-3xl font-bold text-foreground text-center mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">

            <div className="border-b border-cosmic-gold/20 pb-4">
              <h3 className="text-lg font-semibold text-foreground">Will the report be completely personalized?</h3>
              <p>✔️ Yes, it will be created based on your unique birth chart (Janam Kundli).</p>
            </div>

            <div className="border-b border-cosmic-gold/20 pb-4">
              <h3 className="text-lg font-semibold text-foreground">Will the remedies be simple and practical?</h3>
              <p>✔️ Yes, only easy-to-follow mantras, gemstones, poojas, and Rudrakshas will be suggested.</p>
            </div>

            <div className="border-b border-cosmic-gold/20 pb-4">
              <h3 className="text-lg font-semibold text-foreground">Will the pblueictions be based on Dasha and Transit?</h3>
              <p>✔️ Yes, all forecasts are prepablue using precise planetary periods (Dasha) and transits.</p>
            </div>

          </div>
        </Card>

        {/* Final CTA */}
        <Card className="p-6 md:p-12 bg-gradient-to-b from-cosmic-gold/10 to-cosmic-glow/10 border-cosmic-gold/30 backdrop-blur-sm text-center">
          <div className="space-y-6">
            <Badge className="bg-gradient-to-b from-cosmic-gold to-cosmic-gold-dark text-primary-foreground px-4 py-2 text-lg">
              Special Launch Offer: <span className="line-through opacity-80 mx-2">₹1996</span> ₹996 (Offer End Soon)
            </Badge>

            <div className="text-4xl font-bold text-foreground">
              <span className="line-through text-muted-foreground mr-4">₹1996</span>
              <span className="text-cosmic-gold">₹996</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <Button onClick={handleScrollToPricing} variant="cosmic" size="xl" className="flex-1">
                Get My Life Journey Report Now
              </Button>
              
            </div>
          </div>
        </Card>
      </div>
      {/* Countdown Timer */}
      <div className={`fixed inset-x-0 bottom-0 z-50  bg-gradient-to-b from-cosmic-gold to-cosmic-glow border-t border-cosmic-gold/40 shadow-2xl pb-[env(safe-area-inset-bottom)] transition-transform duration-300 md:block ${
          hideFooter ? "translate-y-full" : "translate-y-0"
        } md:translate-y-0`}>
        <div className="mx-auto w-full max-w-screen-xl px-3 sm:px-6 py-2 sm:py-3">
          <div className="flex flex-col sm:flex-row items-center sm:justify-center gap-2 sm:gap-6">
            
            {/* Price (show only final price on mobile) */}
            <div className="text-primary-foreground text-center sm:text-left leading-none">
              <span className="inline-block text-sm sm:text-base line-through opacity-80 px-2">₹1996</span>
              <span className="inline-block text-xl sm:text-3xl font-bold sm:ml-2">₹996</span>
            </div>

            {/* Button */}
            <Button
              onClick={handleScrollToPricing}
              variant="secondary"
              className="w-full sm:w-auto h-10 sm:h-12 px-4 sm:px-6 text-sm sm:text-base bg-cosmic-blue text-cosmic-gold hover:bg-cosmic-blue-light"
            >
              GET YOUR REPORT NOW
            </Button>

            <div className="text-primary-foreground text-center sm:text-left leading-none">
              <span className="text-sm">Offer ends soon in:</span>
              <div className="text-base sm:text-2xl font-bold tabular-nums">{display}</div>
            </div>

          </div>
        </div>
      </div>    
    </section>
    
  );
};